/**
 * Date utility functions for consistent date parsing and formatting
 * across the application.
 * 
 * IMPORTANT: All dates stored internally as ISO UTC (YYYY-MM-DD or ISO 8601)
 * Formatting is applied only at render time based on user's country.
 */

/**
 * Country codes supported by the app
 * Date formats:
 * - AU, NZ, UK, CA, Other: DD/MM/YYYY
 * - US: MM/DD/YYYY
 */
const US_FORMAT_COUNTRIES = ['US'];

/**
 * Format a date based on user's country setting
 * @param {string|Date|number} date - ISO date string, Date object, or timestamp
 * @param {string} country - Country code (AU, NZ, UK, CA, US, Other)
 * @param {object} options - Formatting options
 * @param {boolean} options.includeDay - Include day (default: true)
 * @param {boolean} options.shortMonth - Use short month name (default: false)
 * @param {boolean} options.shortYear - Use 2-digit year (default: false)
 * @returns {string} Formatted date string
 */
export const formatDateByCountry = (date, country = 'AU', options = {}) => {
  if (!date) return '';
  
  const { includeDay = true, shortMonth = false, shortYear = false } = options;
  
  // Parse the date
  let dateObj;
  if (typeof date === 'string') {
    // Handle ISO date string (YYYY-MM-DD or full ISO 8601)
    if (date.includes('T')) {
      dateObj = new Date(date);
    } else {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(Date.UTC(year, month - 1, day || 1));
    }
  } else if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) return '';
  
  const day = dateObj.getUTCDate();
  const month = dateObj.getUTCMonth() + 1; // 0-indexed
  const year = shortYear 
    ? String(dateObj.getUTCFullYear()).slice(-2) 
    : dateObj.getUTCFullYear();
  
  const monthStr = shortMonth 
    ? dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
    : String(month).padStart(2, '0');
  
  const dayStr = String(day).padStart(2, '0');
  
  // Apply country-specific format
  const isUS = US_FORMAT_COUNTRIES.includes(country);
  
  if (!includeDay) {
    // Month/Year only format for charts
    return shortMonth 
      ? `${monthStr} ${year}`
      : (isUS ? `${monthStr}/${year}` : `${monthStr}/${year}`);
  }
  
  if (shortMonth) {
    // Format: "15 Apr 2025" or "Apr 15, 2025" for US
    return isUS 
      ? `${monthStr} ${day}, ${year}`
      : `${day} ${monthStr} ${year}`;
  }
  
  // Numeric format: DD/MM/YYYY or MM/DD/YYYY
  return isUS 
    ? `${monthStr}/${dayStr}/${year}`
    : `${dayStr}/${monthStr}/${year}`;
};

/**
 * Format date for chart X-axis labels (Month Year format)
 * @param {string} dateString - ISO format date string (YYYY-MM-DD)
 * @param {string} country - Country code (optional, for future locale support)
 * @returns {string} Formatted date as "MMM YYYY" (e.g., "Mar 2023")
 */
export const formatChartDate = (dateString, country = 'AU') => {
  return formatDateByCountry(dateString, country, { 
    includeDay: false, 
    shortMonth: true, 
    shortYear: false 
  });
};

/**
 * Format date for chart X-axis labels with short year (Month YY format)
 * @param {string|number|Date} date - Date to format
 * @param {string} country - Country code
 * @returns {string} Formatted date as "MMM YY" (e.g., "Mar 23")
 */
export const formatChartDateShort = (date, country = 'AU') => {
  return formatDateByCountry(date, country, { 
    includeDay: false, 
    shortMonth: true, 
    shortYear: true 
  });
};

/**
 * Parse an ISO date string and format for display in tables
 * @param {string} dateString - ISO format date string (YYYY-MM-DD)
 * @param {string} country - Country code
 * @returns {string} Formatted date based on country (e.g., "15/04/2025" or "04/15/2025")
 */
export const formatDisplayDate = (dateString, country = 'AU') => {
  return formatDateByCountry(dateString, country, { 
    includeDay: true, 
    shortMonth: false, 
    shortYear: false 
  });
};

/**
 * Format date with short month name for display
 * @param {string} dateString - ISO format date string (YYYY-MM-DD)
 * @param {string} country - Country code
 * @returns {string} Formatted date (e.g., "15 Apr 2025" or "Apr 15, 2025" for US)
 */
export const formatDisplayDateWithMonth = (dateString, country = 'AU') => {
  return formatDateByCountry(dateString, country, { 
    includeDay: true, 
    shortMonth: true, 
    shortYear: false 
  });
};

/**
 * Parse an ISO date string safely
 * @param {string} dateString - ISO format date string (YYYY-MM-DD)
 * @returns {Date} UTC Date object
 */
export const parseISODate = (dateString) => {
  if (!dateString) return null;
  
  if (dateString.includes('T')) {
    return new Date(dateString);
  }
  
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day || 1));
};

/**
 * Get today's date in the user's LOCAL timezone as YYYY-MM-DD.
 * (new Date().toISOString() returns UTC and shifts the date for AU/US users.)
 * @returns {string} Local today as YYYY-MM-DD
 */
export const getLocalTodayISO = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
